import { useCallback, useEffect } from 'react';
import { Crosstab } from '@hotjar/webapp/utils/crosstab';
import { MaintenancePage } from '@hotjar/insights-common/components/MaintenancePage';
import type { Store } from '@hotjar/recordings/src/store/player/types';

import {
  readOnly,
  onRecordingDeleted,
  onFeatureMaintenanceMode,
  initialize,
} from '@hotjar/recordings/src/features/Playbacks/utils';
import shallow from 'zustand/shallow';
import { useStore } from '@hotjar/recordings/src/store/player/context';
import { useBodyVisibilityClasses } from './hooks/useBodyVisibilityClasses';
import { ErrorPage } from '../ErrorPage';
import { useCacheNextAndPreviousRecordingRequest } from './hooks/useCacheNextAndPreviousRecordingRequest';
import { useAddErrorCapturingConsentUA } from './hooks/useAddErrorCapturingConsentUA';

import { TopSection } from './components/TopSection';
import { CenterSection } from './components/CenterSection';
import { BottomSection } from './components/BottomSection';

const useEventListeners = (recordingStore: Store) => {
  const onFeatureMaintenanceModeFn = useCallback(
    () => onFeatureMaintenanceMode(recordingStore),
    [recordingStore],
  );

  useEffect(() => {
    initialize(recordingStore);

    Crosstab.listen(
      'recordingDeleted',
      (data) =>
        onRecordingDeleted(
          recordingStore,
          data as { recordingDeletedFromThisTab: boolean; recordingId: number },
        ),
      true,
    );

    window.addEventListener('feature-maintenance-mode', onFeatureMaintenanceModeFn);

    return () => {
      window.removeEventListener('feature-maintenance-mode', onFeatureMaintenanceModeFn);
    };
  }, [onFeatureMaintenanceModeFn, recordingStore]);
};

export const PlayerPage = (): JSX.Element | null => {
  const playerStore = useStore();
  const { maintenanceMode } = playerStore.getState();

  // Must be reactive
  const { site, done, loaded, user, isLightPlayer } = playerStore(
    (state) => ({
      site: state.site,
      done: state.done,
      loaded: state.loaded,
      user: state.user,
      isLightPlayer: state.isLightPlayer,
    }),
    shallow,
  );
  const isReadOnly = readOnly(playerStore);
  const iframeControllerError = window.iframeController.getError();

  useEventListeners(playerStore);
  useBodyVisibilityClasses(loaded, done, isReadOnly, Boolean(iframeControllerError));
  useCacheNextAndPreviousRecordingRequest(playerStore, loaded);
  useAddErrorCapturingConsentUA(user?.user_id, site?.id);

  if (iframeControllerError) return <ErrorPage error={iframeControllerError} />;
  if (maintenanceMode) return <MaintenancePage />;

  return (
    <div className="recording-player">
      <TopSection />
      <CenterSection />
      {!isLightPlayer ? <BottomSection /> : null}
    </div>
  );
};
